/**
 * Servicio del catálogo SAT para el gestor admin (permiso `admin.catalogos_sat`).
 * El listado usa GET /api/admin/claves-sat (paginado + filtros en el servidor · el catálogo del SAT tiene
 * ~52 mil claves) y la actualización desde el XLS oficial usa /api/admin/claves-sat/actualizaciones/*.
 * Los selectores del wizard usan /api/catalogos/claves-sat (búsqueda) · ver `use-claves-sat`.
 */
import { axiosClient } from "@/shared/api/axios-client";
import type { ClaveSat } from "@/lib/types";

/** Clave con todas las columnas del catálogo oficial del SAT. */
export interface ClaveSatAdmin {
  clave: string;
  descripcion: string;
  /** «Opcional», «Sí» o «No» (null en altas manuales). */
  incluirIvaTrasladado: string | null;
  incluirIepsTrasladado: string | null;
  complementoQueDebeIncluir: string | null;
  fechaInicioVigencia: string | null;
  fechaFinVigencia: string | null;
  estimuloFranjaFronteriza: boolean;
  palabrasSimilares: string | null;
  /** Viene en el último catálogo del SAT importado. */
  vigente: boolean;
  /** Se puede usar hoy (vigente y dentro de su ventana de vigencia). */
  utilizable: boolean;
  actualizadoSatAt: string | null;
}

export type EstadoClaveSatFiltro = "todas" | "utilizables" | "noUtilizables";

export interface ListarClavesSatParams {
  busqueda?: string;
  estado?: EstadoClaveSatFiltro;
  soloFranjaFronteriza?: boolean;
  page?: number;
  pageSize?: number;
}

/** Envelope paginado del backend + totales del catálogo completo para el encabezado. */
export interface PaginaClavesSat {
  items: ClaveSatAdmin[];
  total: number;
  page: number;
  pageSize: number;
  totalCatalogo: number;
  totalUtilizables: number;
}

export async function listarClavesSatAdmin(params: ListarClavesSatParams = {}): Promise<PaginaClavesSat> {
  const { data } = await axiosClient.get<PaginaClavesSat>("/api/admin/claves-sat", {
    // «todas» es el valor por omisión: no se manda.
    params: { ...params, estado: params.estado && params.estado !== "todas" ? params.estado : undefined },
  });
  return data;
}

export interface CrearClaveSatInput {
  clave: string;
  descripcion: string;
}

export async function crearClaveSat(input: CrearClaveSatInput): Promise<ClaveSat> {
  const { data } = await axiosClient.post<ClaveSat>("/api/admin/claves-sat", input);
  return data;
}

export async function editarClaveSat(clave: string, descripcion: string): Promise<ClaveSat> {
  const { data } = await axiosClient.patch<ClaveSat>(`/api/admin/claves-sat/${encodeURIComponent(clave)}`, { descripcion });
  return data;
}

// --- Actualización del catálogo desde el XLS oficial del SAT ---------------------------------

export type OrigenActualizacion = "Sat" | "Archivo" | "Programada";
export type EstadoActualizacion = "EnCurso" | "Completada" | "SinCambios" | "Fallida";

export interface ActualizacionClavesSat {
  id: string;
  origen: OrigenActualizacion;
  estado: EstadoActualizacion;
  iniciadaAt: string;
  terminadaAt: string | null;
  solicitadaPorEmail: string | null;
  archivoNombre: string | null;
  urlOrigen: string | null;
  fechaPublicacionCatalogo: string | null;
  totalEnArchivo: number;
  creadas: number;
  actualizadas: number;
  sinCambios: number;
  descontinuadas: number;
  error: string | null;
}

export interface PaginaActualizacionesClavesSat {
  items: ActualizacionClavesSat[];
  total: number;
  page: number;
  pageSize: number;
  /** Hay una actualización ejecutándose · el gestor consulta cada pocos segundos hasta que termine. */
  enCurso: boolean;
}

export async function listarActualizacionesClavesSat(pageSize = 10): Promise<PaginaActualizacionesClavesSat> {
  const { data } = await axiosClient.get<PaginaActualizacionesClavesSat>("/api/admin/claves-sat/actualizaciones", {
    params: { page: 1, pageSize },
  });
  return data;
}

/** Descarga el catálogo vigente del sitio del SAT y lo aplica (corre en segundo plano). */
export async function solicitarActualizacionDesdeSat(): Promise<ActualizacionClavesSat> {
  const { data } = await axiosClient.post<ActualizacionClavesSat>("/api/admin/claves-sat/actualizaciones/sat");
  return data;
}

/** Aplica un XLS descargado a mano (respaldo si el sitio del SAT no responde). El archivo pesa ~48 MB. */
export async function subirCatalogoClavesSat(archivo: File): Promise<ActualizacionClavesSat> {
  const form = new FormData();
  form.append("archivo", archivo);
  const { data } = await axiosClient.post<ActualizacionClavesSat>("/api/admin/claves-sat/actualizaciones/archivo", form, {
    timeout: 10 * 60_000,
  });
  return data;
}
